import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';

export function GDPRConsentModal({ open, onAccept }) {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);

  const handleAccept = () => {
    if (!acceptedTerms || !acceptedPrivacy) {
      return; // Required consents not accepted
    }

    onAccept({
      terms: acceptedTerms,
      privacy: acceptedPrivacy,
      marketing: acceptedMarketing,
      data_processing: true // Implied with privacy
    });
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl">Welcome to PayMig</DialogTitle>
          <DialogDescription>
            Before you continue, please review and accept our terms
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-6">
            {/* Terms of Service */}
            <div className="space-y-2">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="terms"
                  checked={acceptedTerms}
                  onCheckedChange={setAcceptedTerms}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="text-base font-medium">
                    <label htmlFor="terms" className="cursor-pointer">I accept the </label>
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-600 underline hover:text-teal-700 relative z-10"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Terms of Service
                    </a>
                    <span className="text-red-500 ml-1">*</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    By using PayMig, you agree to our terms governing the use of the service,
                    payment processing, and account management.
                  </p>
                </div>
              </div>
            </div>

            {/* Privacy Policy */}
            <div className="space-y-2">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="privacy"
                  checked={acceptedPrivacy}
                  onCheckedChange={setAcceptedPrivacy}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="text-base font-medium">
                    <label htmlFor="privacy" className="cursor-pointer">I accept the </label>
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-600 underline hover:text-teal-700 relative z-10"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Privacy Policy
                    </a>
                    <span className="text-red-500 ml-1">*</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    I consent to the collection and processing of my personal data as described
                    in the Privacy Policy, including payment information and transaction history.
                  </p>
                </div>
              </div>
            </div>

            {/* GDPR Notice */}
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <h4 className="font-semibold text-sm mb-2">Your Data Rights</h4>
              <p className="text-xs text-muted-foreground">
                Under GDPR, you have the right to access, rectify, export, and delete your personal
                data. You can exercise these rights at any time in your account settings.
              </p>
            </div>

            {/* Marketing (Optional) */}
            <div className="space-y-2">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="marketing"
                  checked={acceptedMarketing}
                  onCheckedChange={setAcceptedMarketing}
                  className="mt-1"
                />
                <div className="flex-1">
                  <Label htmlFor="marketing" className="text-base font-medium cursor-pointer">
                    I consent to receiving marketing communications{' '}
                    <span className="text-sm text-muted-foreground">(optional)</span>
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Receive updates about new features, promotions, and PayMig news. You can
                    unsubscribe at any time.
                  </p>
                </div>
              </div>
            </div>

            {/* Required Notice */}
            <div className="text-xs text-muted-foreground">
              <span className="text-red-500">*</span> Required to use PayMig
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button
            onClick={handleAccept}
            disabled={!acceptedTerms || !acceptedPrivacy}
            className="w-full sm:w-auto"
            size="lg"
          >
            {!acceptedTerms || !acceptedPrivacy
              ? 'Please accept required terms'
              : 'Continue to PayMig'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
